import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Clock, FileSignature, FileText, ClipboardList, ArrowRight, XCircle } from "lucide-react";

const STEP_ICON = { done: CheckCircle2, current: Clock, todo: Circle };
const STEP_COLOR = { done: "text-emerald-500", current: "text-[#2EA3FD]", todo: "text-slate-300" };

function Steps({ steps }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="portal-steps">
      {steps.map((s, i) => {
        const Icon = STEP_ICON[s.state] || Circle;
        return (
          <div key={s.key} className="flex items-center gap-2" data-testid={`portal-step-${s.key}`}>
            <div className="flex items-center gap-2">
              <Icon size={18} className={STEP_COLOR[s.state]} />
              <span className={`text-xs font-medium ${s.state === "todo" ? "text-slate-400" : "text-[#0B1A2F]"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <span className="w-6 h-px bg-slate-200 hidden sm:block" />}
          </div>
        );
      })}
    </div>
  );
}

function DocCard({ d }) {
  const link = d.kind === "offer" ? `/offer/${d.sign_token}` : `/contract/${d.sign_token}`;
  const Icon = d.kind === "offer" ? FileText : FileSignature;
  const statusText = {
    sent: "Awaiting your response", signed: "Signed", declined: "Declined",
    awaiting_countersign: "Signed — awaiting CASS countersignature", countersigned: "Fully signed",
  }[d.status] || d.status;
  const tone = d.status === "sent" ? "amber" : (d.status === "declined" ? "rose" : "emerald");
  const toneC = { amber: "bg-amber-50 border-amber-200", rose: "bg-rose-50 border-rose-200", emerald: "bg-emerald-50 border-emerald-200" }[tone];
  return (
    <div className={`rounded-xl border p-5 ${toneC}`} data-testid={`portal-${d.kind}-${d.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#0B1A2F] font-semibold"><Icon size={16} className="text-[#2EA3FD]" />{d.kind === "offer" ? "Job offer" : "Employment contract"}</div>
          <p className="text-sm text-slate-600 mt-1">{d.job_title}{d.start_date ? ` · starts ${fmtDate(d.start_date)}` : ""}</p>
          <p className="text-xs mt-1 font-medium text-slate-500">{statusText}</p>
        </div>
        {d.sign_token && (
          <Button asChild size="sm" className="bg-[#2EA3FD] hover:bg-[#1B8EE6] shrink-0"><Link to={link} data-testid={`portal-${d.kind}-action-${d.id}`}>Review &amp; sign<ArrowRight size={15} className="ml-1.5" /></Link></Button>
        )}
      </div>
    </div>
  );
}

export default function CandidatePortal() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/portal/${token}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [token]);

  if (err) return <div className="min-h-screen grid place-items-center bg-[#EEF2F7] p-6"><div className="bg-white border border-slate-200 rounded-xl p-10 text-center max-w-md"><p className="text-lg font-semibold text-[#0B1A2F]">Link unavailable</p><p className="text-sm text-slate-500 mt-2">This tracking link isn't valid. Please use the most recent link we sent you.</p></div></div>;
  if (!d) return <div className="min-h-screen grid place-items-center text-slate-400" data-testid="portal-loading">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#EEF2F7]" data-testid="candidate-portal">
      <header className="h-16 bg-gradient-to-r from-[#091E36] via-[#0B1A2F] to-[#0F2C50] text-white flex items-center px-6">
        <span className="text-2xl font-extrabold text-[#2EA3FD]">CASS</span><span className="ml-2 text-lg font-semibold">Careers</span>
      </header>
      <div className="max-w-3xl mx-auto p-6 space-y-5">
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#2EA3FD]">Your application</p>
          <h1 className="text-2xl font-extrabold text-[#0B1A2F] mt-1" data-testid="portal-greeting">Hi {d.first_name}, welcome back</h1>
          <p className="text-sm text-slate-500 mt-0.5">{d.vacancy_title}{d.applied_at ? ` · applied ${fmtDate(d.applied_at)}` : ""}</p>
          <div className={`mt-4 rounded-lg px-4 py-3 text-sm ${d.rejected ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-[#F5FAFF] text-[#0B1A2F] border border-sky-100"}`} data-testid="portal-status-message">
            {d.rejected && <XCircle size={15} className="inline mr-1.5 -mt-0.5 text-rose-500" />}{d.status_message}
          </div>
        </div>

        {!d.rejected && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-semibold text-[#0B1A2F] mb-4">Progress</h2>
            <Steps steps={d.steps} />
          </div>
        )}

        {(d.offers?.length > 0 || d.contracts?.length > 0) && (
          <div className="space-y-3" data-testid="portal-documents">
            <h2 className="font-semibold text-[#0B1A2F]">Offers &amp; contracts</h2>
            {d.offers.map((o) => <DocCard key={o.id} d={o} />)}
            {d.contracts.map((c) => <DocCard key={c.id} d={c} />)}
          </div>
        )}

        {d.onboarding && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="portal-onboarding">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-[#0B1A2F] font-semibold"><ClipboardList size={16} className="text-[#36776B]" />Onboarding</div>
                <p className="text-sm text-slate-600 mt-1">{d.onboarding.status === "completed" ? "Your onboarding details have been submitted. Thank you!" : "Please complete your new-starter details so we can set up your record."}</p>
              </div>
              {d.onboarding.token && (
                <Button asChild size="sm" className="bg-[#36776B] hover:bg-[#2C6055] shrink-0"><Link to={`/onboarding/${d.onboarding.token}`} data-testid="portal-onboarding-action">Complete form<ArrowRight size={15} className="ml-1.5" /></Link></Button>
              )}
            </div>
          </div>
        )}

        {d.timeline?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="portal-timeline">
            <h2 className="font-semibold text-[#0B1A2F] mb-3">Activity</h2>
            <div className="space-y-2.5">
              {d.timeline.map((t, i) => (
                <div key={i} className="flex items-center gap-3 text-sm" data-testid={`portal-timeline-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2EA3FD] shrink-0" />
                  <span className="text-slate-600">{t.label}</span>
                  <span className="text-xs text-slate-400 ml-auto">{fmtDate(t.at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
